import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'

export const useWaitlistStore = defineStore('waitlist', () => {
  // ⬇️ Клиенты создаются здесь (внутри стора)
  const apiPublic = axios.create({
    baseURL: (import.meta.env.VITE_API_URL || '') + '/api/public'
  })

  const api = axios.create({
    baseURL: (import.meta.env.VITE_API_URL || '') + '/api'
  })

  api.interceptors.request.use(config => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  })

  // ===== Остальной код (без изменений) =====
  const entries = ref([])
  const activeCount = ref(0)
  const offeredCount = ref(0)
  const totalCount = ref(0)
  const isLoading = ref(false)

  const activeEntries = computed(() => entries.value.filter(e => e.status === 'active'))
  const offeredEntries = computed(() => entries.value.filter(e => e.status === 'offered'))
  const historyEntries = computed(() => entries.value.filter(e => 
    ['booked', 'expired', 'cancelled', 'converted'].includes(e.status)
  ))

  async function loadAllStatuses() {
    isLoading.value = true
    try {
      const [activeRes, offeredRes, historyRes] = await Promise.all([
        api.get('/waitlist?status=active'),
        api.get('/waitlist?status=offered'),
        api.get('/waitlist?status=history')
      ])
      entries.value = [
        ...(activeRes.data || []),
        ...(offeredRes.data || []),
        ...(historyRes.data || [])
      ]
    } catch (err) {
      console.error('Ошибка загрузки waitlist:', err)
    } finally {
      isLoading.value = false
    }
  }

  async function loadCounts() {
    try {
      const { data } = await api.get('/waitlist/count')
      activeCount.value = parseInt(data.active_count) || 0
      offeredCount.value = parseInt(data.offered_count) || 0
      totalCount.value = parseInt(data.total_count) || 0
    } catch (err) {
      console.error('Ошибка загрузки счётчиков:', err)
    }
  }

  async function getById(id) {
    try {
      const { data } = await api.get(`/waitlist/${id}`)
      return data
    } catch (err) {
      console.error('Ошибка загрузки заявки:', err)
      return null
    }
  }

  async function create(payload) {
    try {
      const { data } = await api.post('/waitlist', payload)
      entries.value.push(data)
      await loadCounts()
      return data
    } catch (err) {
      console.error('Ошибка создания:', err)
      throw err
    }
  }

  async function update(id, payload) {
    try {
      const { data } = await api.put(`/waitlist/${id}`, payload)
      const idx = entries.value.findIndex(e => e.id === id)
      if (idx !== -1) entries.value[idx] = data
      return data
    } catch (err) {
      console.error('Ошибка обновления:', err)
      throw err
    }
  }

  async function remove(id) {
    try {
      await api.delete(`/waitlist/${id}`)
      entries.value = entries.value.filter(e => e.id !== id)
      await loadCounts()
    } catch (err) {
      console.error('Ошибка удаления:', err)
      throw err
    }
  }

  async function offerSlot(id, slotDate, slotTime, masterId) {
    try {
      const { data } = await api.post(`/waitlist/${id}/offer`, {
        slot_date: slotDate,
        slot_time: slotTime,
        master_id: masterId
      })
      const idx = entries.value.findIndex(e => e.id === id)
      if (idx !== -1) entries.value[idx] = data
      return data
    } catch (err) {
      console.error('Ошибка предложения:', err)
      throw err
    }
  }

  async function acceptOffer(id) {
    try {
      const { data } = await api.post(`/waitlist/${id}/accept`)
      const idx = entries.value.findIndex(e => e.id === id)
      if (idx !== -1) entries.value[idx] = data
      return data
    } catch (err) {
      console.error('Ошибка принятия:', err)
      throw err
    }
  }

  async function declineOffer(id) {
    try {
      const { data } = await api.post(`/waitlist/${id}/decline`)
      const idx = entries.value.findIndex(e => e.id === id)
      if (idx !== -1) entries.value[idx] = data
      return data
    } catch (err) {
      console.error('Ошибка отклонения:', err)
      throw err
    }
  }

  async function convertToBooking(id, bookingId) {
    try {
      const { data } = await api.post(`/waitlist/${id}/convert`, { booking_id: bookingId })
      const idx = entries.value.findIndex(e => e.id === id)
      if (idx !== -1) entries.value[idx] = data
      return data
    } catch (err) {
      console.error('Ошибка конвертации:', err)
      throw err
    }
  }

  async function findMatches(slotDate, slotTime, masterId, serviceId) {
    try {
      const { data } = await api.get('/waitlist/match', {
        params: { date: slotDate, time: slotTime, master_id: masterId, service_id: serviceId }
      })
      return data
    } catch (err) {
      console.error('Ошибка поиска:', err)
      return []
    }
  }

  async function createPublic(payload) {
    const { data } = await apiPublic.post('/waitlist', payload)
    return data
  }

  async function getPosition(id) {
    const { data } = await apiPublic.get(`/waitlist/${id}/position`)
    return data
  }

  async function acceptPublic(id) {
    const { data } = await apiPublic.post(`/waitlist/${id}/accept`)
    return data
  }

  async function declinePublic(id) {
    const { data } = await apiPublic.post(`/waitlist/${id}/decline`)
    return data
  }

  function $reset() {
    entries.value = []
    activeCount.value = 0
    offeredCount.value = 0
    totalCount.value = 0
  }

  return {
    entries, activeCount, offeredCount, totalCount, isLoading,
    activeEntries, offeredEntries, historyEntries,
    loadAllStatuses, loadCounts, getById, create, update, remove,
    offerSlot, acceptOffer, declineOffer, convertToBooking,
    findMatches,
    createPublic, getPosition, acceptPublic, declinePublic,
    $reset
  }
})